import {
  Phase,
  PhaseDuration,
  ResolvedPhase,
  DisabledReasonType
} from 'modules/Cube/domain/cube.domain.types'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { dequal } from 'dequal'
import isObject from 'lodash/fp/isObject'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { arrayToIdKeyedObject, usePrevious } from '@sequencehq/utils'
import { scheduleStatusAdapter } from 'Cube/utils/status.adapter'
import { duplicatePhase } from 'Cube/utils/duplicatePhase'
import { usePhasePermissions } from 'Cube/utils/usePhasePermissions'
import { match } from 'ts-pattern'
import { useAtom } from 'jotai'
import { phaseExpansionAtom } from './phaseEditor.atoms'
import {
  getExistingPhaseExpansion,
  getIsPhaseExpandedByDefault,
  getIsPhaseHighlighted
} from './phaseEditor.helpers'

export const PHASE_INSERT_FLASH_MS = 600

const ONE_YEAR_DURATION = { years: 1, months: 0, days: -1 }

type usePricingWidget = () => {
  phases: (ResolvedPhase & {
    expanded: boolean
    focusDurationOnLoad: boolean
    isNewlyInserted: boolean
  })[]
  addPhase: {
    disabled: boolean
    handler: () => void
    disabledReason: string
  }
  insertPhaseBefore: (phaseId: string) => void
  updatePhaseExpanded: (phaseId: Phase['id']) => (expanded: boolean) => void
}

export const usePricingWidget: usePricingWidget = () => {
  const cubeContext = useCubeContext()
  // BlockNote remounts the phase block when drawers open, so this state lives
  // in the editor-level provider rather than in the block itself.
  const [phaseExpansion, setPhaseExpansion] = useAtom(phaseExpansionAtom)
  const [focusDurationOnLoadPhaseId, setFocusDurationOnLoadPhaseId] = useState<
    Phase['id'] | null
  >(null)
  const [newlyInsertedPhaseId, setNewlyInsertedPhaseId] = useState<
    string | null
  >(null)
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    }
  }, [])

  const sortedPhases = useMemo(() => {
    return cubeContext.queries.orderedPhases
  }, [cubeContext])

  useEffect(() => {
    if (!focusDurationOnLoadPhaseId) return

    const hasFocusTargetLoaded = sortedPhases.some(
      phase => phase.id === focusDurationOnLoadPhaseId
    )
    if (!hasFocusTargetLoaded) return

    // Child layout effects have now consumed this one-shot request. Clearing it
    // prevents a later BlockNote remount from requesting focus again.
    setFocusDurationOnLoadPhaseId(null)
  }, [focusDurationOnLoadPhaseId, sortedPhases])

  /**
   * If the phases change (e.g. we saved the phases and gained new ids) then
   * rebuild the expansion state with the new ids and retain the old values as
   * much as possible. This keeps an expanded phase open after a save reloads it.
   */
  const previousHighlightedEntityIds = usePrevious(
    cubeContext.queries.rawData.editor.highlightedEntityIds
  )
  const dealType = cubeContext.queries.rawData.data.quote.dealType
  const previousDealType = usePrevious(dealType)
  useEffect(() => {
    const currentIds = phaseExpansion.map(({ id }) => id)
    const sortedPhaseIds = sortedPhases.map(({ id }) => id)
    /**
     * Applying an amendment swaps in the amended schedule's phases without
     * remounting, so we start them from the defaults rather than carrying
     * over whatever the quote's old phases had open.
     */
    const hasBecomeAnAmendment =
      previousDealType !== undefined &&
      dealType === 'AMENDMENT' &&
      previousDealType !== 'AMENDMENT'
    const havePhaseIdsChanged = !dequal(currentIds, sortedPhaseIds)
    const haveHighlightedEntityIdsChanged = !dequal(
      previousHighlightedEntityIds,
      cubeContext.queries.rawData.editor.highlightedEntityIds
    )
    const shouldRebuildPhaseExpansion =
      havePhaseIdsChanged ||
      haveHighlightedEntityIdsChanged ||
      hasBecomeAnAmendment

    if (shouldRebuildPhaseExpansion) {
      /**
       * Built from the latest state rather than `phaseExpansion`, as a render
       * can land before an earlier update here is applied, and the stale copy
       * would undo it (e.g. the amendment reset above).
       */
      setPhaseExpansion(previousPhaseExpansion =>
        sortedPhases.map((phase, idx) => {
          /**
           * This fallback lets us retain expansion by id when a phase is
           * deleted, and by position when saving replaces temporary ids.
           */
          const existingExpansion = getExistingPhaseExpansion({
            phaseExpansion: previousPhaseExpansion,
            phaseId: phase.id,
            idx
          })
          const isExpanded = hasBecomeAnAmendment
            ? getIsPhaseExpandedByDefault({
                phase,
                idx,
                sortedPhases,
                amendmentPhaseId: cubeContext.queries.amendment.phaseId
              })
            : (existingExpansion?.expanded ??
              getIsPhaseExpandedByDefault({
                phase,
                idx,
                sortedPhases,
                amendmentPhaseId: cubeContext.queries.amendment.phaseId
              }))

          return {
            id: phase.id,
            expanded:
              getIsPhaseHighlighted(
                phase,
                cubeContext.queries.rawData.editor.highlightedEntityIds
              ) || isExpanded
          }
        })
      )
    }
  }, [
    sortedPhases,
    phaseExpansion,
    cubeContext.queries.rawData.editor.highlightedEntityIds,
    cubeContext.queries.amendment.phaseId,
    previousHighlightedEntityIds,
    dealType,
    previousDealType,
    setPhaseExpansion
  ])

  const basePhaseData = useMemo(() => {
    return sortedPhases.map((phase, idx) => {
      const existingExpansion = getExistingPhaseExpansion({
        phaseExpansion,
        phaseId: phase.id,
        idx
      })

      return {
        ...phase,
        // A single available phase should never be collapsible.
        expanded:
          sortedPhases.length === 1
            ? true
            : (existingExpansion?.expanded ?? false),
        focusDurationOnLoad: phase.id === focusDurationOnLoadPhaseId
      }
    })
  }, [focusDurationOnLoadPhaseId, phaseExpansion, sortedPhases])

  const enhancedPhaseData = useMemo(() => {
    return basePhaseData.map(phase => ({
      ...phase,
      isNewlyInserted: phase.id === newlyInsertedPhaseId
    }))
  }, [basePhaseData, newlyInsertedPhaseId])

  /**
   * Whether a new phase resets its billing periods or co-terms with the phase
   * before it is an account setting, captured into the configuration by the
   * load. Quotes cannot persist the preference, so theirs are pinned to
   * continuing.
   */
  const newPhaseRecurrencePreference =
    cubeContext.queries.rawData.configuration.editorDefaults.resetBillingPeriod

  const addPhaseHandler = useCallback(() => {
    const previousPhase = cubeContext.queries.orderedPhases.at(-1)
    if (
      !cubeContext.queries.availableFeatures.common.addPhase.available
        .enabled ||
      !previousPhase
    ) {
      return
    }

    const newPhaseData = duplicatePhase({
      phase: previousPhase,
      recurrencePreference: newPhaseRecurrencePreference
    })

    cubeContext.mutators.updateData({
      common: {
        phaseIds: [
          ...cubeContext.queries.rawData.data.common.phaseIds,
          newPhaseData.phase.id
        ]
      },
      phases: {
        [previousPhase.id]: {
          duration: previousPhase.dates.duration
        },
        [newPhaseData.phase.id]: {
          ...newPhaseData.phase,
          duration: match(cubeContext.queries.rawData.data.quote.dealType)
            .with(
              'AMENDMENT',
              () =>
                cubeContext.queries.amendment.remainingDuration ??
                getCopiedPhaseDuration(previousPhase)
            )
            .with('NEW_BUSINESS', 'RENEWAL', () =>
              getCopiedPhaseDuration(previousPhase)
            )
            .exhaustive()
        }
      },
      prices: arrayToIdKeyedObject(newPhaseData.prices),
      discounts: arrayToIdKeyedObject(newPhaseData.discounts),
      minimums: arrayToIdKeyedObject(newPhaseData.minimums)
    })

    setPhaseExpansion(previousPhaseExpansion => [
      ...previousPhaseExpansion,
      {
        id: newPhaseData.phase.id,
        expanded: true
      }
    ])
    setFocusDurationOnLoadPhaseId(newPhaseData.phase.id)
  }, [cubeContext, newPhaseRecurrencePreference, setPhaseExpansion])

  const insertPhaseBeforeHandler = useCallback(
    (insertBeforePhaseId: string) => {
      const insertBeforeIndex = cubeContext.queries.orderedPhases.findIndex(
        p => p.id === insertBeforePhaseId
      )
      if (insertBeforeIndex === -1) return

      const phaseToInsertBefore =
        cubeContext.queries.orderedPhases[insertBeforeIndex]
      if (phaseToInsertBefore.prices.length === 0) return

      const phaseIds = cubeContext.queries.rawData.data.common.phaseIds

      const newPhaseData = duplicatePhase({
        phase: phaseToInsertBefore,
        recurrencePreference: newPhaseRecurrencePreference
      })

      const newPhaseIds = [
        ...phaseIds.slice(0, insertBeforeIndex),
        newPhaseData.phase.id,
        ...phaseIds.slice(insertBeforeIndex)
      ]

      // Only the last phase can be open ended, so a phase inserted before it
      // copies the length of the phase before instead
      const phaseToCopy = match(phaseToInsertBefore.dates.duration)
        .with('OPEN_ENDED', () =>
          cubeContext.queries.orderedPhases.slice(0, insertBeforeIndex).at(-1)
        )
        .otherwise(() => phaseToInsertBefore)
      const copiedDuration = phaseToCopy
        ? getCopiedPhaseDuration(phaseToCopy)
        : ONE_YEAR_DURATION

      cubeContext.mutators.updateData({
        common: { phaseIds: newPhaseIds },
        phases: {
          [newPhaseData.phase.id]: {
            ...newPhaseData.phase,
            duration: match(cubeContext.queries.rawData.data.quote.dealType)
              .with('AMENDMENT', () =>
                isObject(cubeContext.queries.amendment.remainingDuration)
                  ? cubeContext.queries.amendment.remainingDuration
                  : copiedDuration
              )
              .with('NEW_BUSINESS', 'RENEWAL', () => copiedDuration)
              .exhaustive()
          }
        },
        prices: arrayToIdKeyedObject(newPhaseData.prices),
        discounts: arrayToIdKeyedObject(newPhaseData.discounts),
        minimums: arrayToIdKeyedObject(newPhaseData.minimums)
      })

      setPhaseExpansion(previousPhaseExpansion => [
        ...previousPhaseExpansion.slice(0, insertBeforeIndex),
        {
          id: newPhaseData.phase.id,
          expanded: true
        },
        ...previousPhaseExpansion.slice(insertBeforeIndex)
      ])
      setFocusDurationOnLoadPhaseId(newPhaseData.phase.id)

      if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
      setNewlyInsertedPhaseId(newPhaseData.phase.id)
      flashTimerRef.current = setTimeout(
        () => setNewlyInsertedPhaseId(null),
        PHASE_INSERT_FLASH_MS
      )
    },
    [cubeContext, newPhaseRecurrencePreference, setPhaseExpansion]
  )

  const updatePhaseExpanded = useCallback(
    (phaseId: Phase['id']) => (expanded: boolean) => {
      setPhaseExpansion(previousPhaseExpansion =>
        previousPhaseExpansion.map(phase =>
          phase.id === phaseId ? { ...phase, expanded } : phase
        )
      )
    },
    [setPhaseExpansion]
  )

  const { canUpdatePhase } = usePhasePermissions()

  const addPhaseDisabledText = useMemo(() => {
    const REASON_MAP: Partial<Record<DisabledReasonType, string>> = {
      [DisabledReasonType.InvalidStatus]:
        'Cannot add a phase with the current status',
      [DisabledReasonType.PhaseHasNoPrices]:
        'Add prices to the latest phase to be able to add a new one',
      [DisabledReasonType.CannotEditBillingSchedule]:
        'You do not have permission to edit billing schedules',
      [DisabledReasonType.AmendedScheduleCovered]:
        'The phases already cover the schedule being amended'
    }

    if (!canUpdatePhase) {
      return REASON_MAP[DisabledReasonType.CannotEditBillingSchedule] ?? ''
    }

    const cubeVariantLabel: string = scheduleStatusAdapter.out(
      cubeContext.queries.rawData.data.common.status
    )
      ? 'schedule'
      : 'quote'

    if (
      cubeContext.queries.availableFeatures.common.addPhase.available.enabled
    ) {
      return ''
    }

    return (
      REASON_MAP[
        cubeContext.queries.availableFeatures.common.addPhase.reasons[0]
          ?.reasonType
      ] ?? `Cannot add a new phase to this ${cubeVariantLabel}`
    )
  }, [
    canUpdatePhase,
    cubeContext.queries.availableFeatures.common.addPhase,
    cubeContext.queries.rawData.data.common.status
  ])

  return {
    phases: enhancedPhaseData,
    addPhase: {
      handler: addPhaseHandler,
      disabled:
        !cubeContext.queries.availableFeatures.common.addPhase.available
          .enabled || !canUpdatePhase,
      disabledReason: addPhaseDisabledText
    },
    insertPhaseBefore: insertPhaseBeforeHandler,
    updatePhaseExpanded
  }
}

/**
 * A new phase copies the length of the phase it's based on, apart from a
 * milestone, which has no set length, so the copy starts out as one year.
 */
const getCopiedPhaseDuration = (phase: ResolvedPhase): PhaseDuration =>
  match(phase.dates.duration)
    .with('MILESTONE', () => ONE_YEAR_DURATION)
    .otherwise(duration => duration)
